import { useCallback, useRef, useState } from 'react'
import api from '@/shared/api/client'
import { useToast } from '@/shared/components/ui/toast'

/**
 * Open Support Codio in a new tab (Support Codio scope.md L19.3).
 *
 * The tab is opened SYNCHRONOUSLY inside the click — a window.open after an
 * await is blocked as a popup — and only navigated once the backend has minted
 * the handoff URL (token in the #fragment, single use, ≤60 s). A blocked popup
 * mints nothing; a failed mint closes the blank tab. A local busy ref
 * serialises clicks so a double-click mints one token.
 */
export function useOpenSupport() {
  const { addToast } = useToast()
  const busy = useRef(false)
  const [pending, setPending] = useState(false)

  const openSupport = useCallback(async () => {
    if (busy.current) return
    busy.current = true
    setPending(true)
    const tab = window.open('', '_blank')
    try {
      if (!tab) {
        addToast({
          variant: 'error',
          title: "Couldn't open Support",
          description: 'Your browser blocked the new tab. Allow pop-ups for this site and try again.',
        })
        return
      }
      tab.opener = null
      try {
        const res = await api.post<{ data: { url: string } }>('/support/handoff')
        const url = res.data.data.url
        if (!/^https?:$/.test(new URL(url).protocol)) throw new Error('bad support url')
        tab.location.replace(url)
      } catch {
        tab.close()
        addToast({ variant: 'error', title: "Couldn't open Support", description: 'Please try again.' })
      }
    } finally {
      busy.current = false
      setPending(false)
    }
  }, [addToast])

  return { openSupport, pending }
}
